import { Button, Card, Col, Divider, Radio, Row, Select, Skeleton, Space, Tooltip } from "antd";
import { ReactNode, useEffect, useState } from "react";
import { getEnvironmentService, getKubeService } from "../../backend";
import { useRecoilState, useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import { AllClusters, Environment, EnvironmentDetails, FormState } from "../../types";
import { ListKubernetesResponseKubernetesesTypeEnum } from "@microtica/ms-kube-sdk";
import ConnectClusterModal from "../modals/ConnectClusterModal";
import { trackKubeConnectInit } from "../../backend/tracking/kubernetes";
import CreateEnvironment from "../CreateEnvironment";
import { currentTemplateStepState, latestTemplateStepState, templateState } from "../../recoil/template";
import { useCardClassName } from "../../contexts/Helpers/helpers";
import { useAuth } from "../../contexts/Auth";
import CreateClusterModal from "../modals/CreateClusterModal";
import { trackTemplateKubernetesAwsCreate, trackTemplateKubernetesConnectExisting, trackTemplateKubernetesSave } from "../../backend/tracking/templates";
import ExplanationButton from "../explanations/ExplanationButton";
import SelectClusterExplanation from "../explanations/SelectClusterExplanation";
import { CreateStageRequestCloudProviderEnum } from "@microtica/ms-engine-sdk";
import MessageCard from "../MessageCard";
import { AWS_REGIONS } from "../../enums/enums";

interface ClusterTemplateModuleProps {
    templateName: string;
    creatingCluster: boolean;
    setCreatingCluster: React.Dispatch<React.SetStateAction<boolean>>;
    initialEnvironment?: EnvironmentDetails;
    onSelect?: (environment: EnvironmentDetails) => void;
    envId?: string;
    clusterAppName?: string;
    setClusterAppName: (clusterAppName?: string) => void;
    disabled?: boolean;
}

const ClusterTemplateModule = ({
    templateName,
    creatingCluster,
    setCreatingCluster,
    initialEnvironment,
    onSelect,
    envId,
    clusterAppName,
    setClusterAppName,
    disabled
}: ClusterTemplateModuleProps) => {
    const { isLoggedIn } = useAuth();
    const currentProject = useRecoilValue(currentProjectState);
    const [loadingClusters, setLoadingClusters] = useState(true);
    const [clusters, setClusters] = useState<AllClusters>({});
    const [namespaces, setNamespaces] = useState<{ id: string; name: string }[]>([]);
    const [loadingNamespaces, setLoadingNamespaces] = useState(false);
    const [clusterExists, setClusterExists] = useState(false);
    const [clusterType, setClusterType] = useState<"own" | "shared" | "connected">(!creatingCluster ? "connected" : "own");
    // const [projectHasSharedCluster, setProjectHasSharedCluster] = useState<boolean>(false);
    const [showConnectClusterModal, setShowConnectClusterModal] = useState(false);
    const [showCreateClusterModal, setShowCreateClusterModal] = useState(false);
    const [defaultEnvironment, setDefaultEnvironment] = useState<Omit<Environment, "cloudProvider">>();
    const [currentTemplateState, setCurrentTemplateState] = useRecoilState(templateState);
    const currentTemplateStep = useRecoilValue(currentTemplateStepState);
    const latestTemplateStep = useRecoilValue(latestTemplateStepState);
    // const [connectedDemoCluster, setConnectedDemoCluster] = useState(false);
    const [selectedEnv, setSelectedEnv] = useState(initialEnvironment);
    const [validationError, setValidationError] = useState<ReactNode>();

    // const [modal, setModal] = useState<{
    //     destroy: () => void;
    //     update: (configUpdate: ModalFuncProps) => void;
    // }>();

    useEffect(() => {
        if (isLoggedIn && !!currentProject) {
            loadData(!initialEnvironment);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, currentProject]);

    useEffect(() => {
        handleSetTemplateState(disabled ? "disabled" : "editing");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled]);


    useEffect(() => {
        if (clusterType === "connected") {
            setCreatingCluster(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clusterType]);

    const cardClassName = useCardClassName(
        disabled || currentTemplateState["cluster"] === "disabled",
        [disabled, currentTemplateState, currentTemplateStep]
    );

    const handleSetTemplateState = (mode: FormState) => {
        setCurrentTemplateState({
            ...currentTemplateState,
            cluster: mode,
            ...(mode === "saved" && { deploy: "editing" })
        });
    }

    const loadData = async (selectDefaultCluster = true, newClusterName?: string) => {
        const { data: { stages: environments } } = await getEnvironmentService().getStages(currentProject!.id);

        const allClusters = (await Promise.all(
            environments.map(env =>
                getKubeService().listKubernetes(currentProject!.id, env.id)
                    .then(({ data: { kuberneteses } }) => ({ ...env, clusters: kuberneteses }))
            )
        )).reduce((allClusters, env) => {
            allClusters[env.id] = {
                clusters: env.clusters.map(cluster => ({
                    id: cluster.id,
                    name: cluster.name,
                    type: cluster.type
                })),
                env: {
                    id: env.id,
                    name: env.name,
                    accountId: env.awsAccountId,
                    region: AWS_REGIONS.find(r => r.value === env.awsRegion)?.id
                }
            };
            return allClusters;
        }, {} as AllClusters);

        const filteredClusters = envId ? { [envId]: allClusters[envId] } : allClusters;
        setClusters(filteredClusters);
        const clusterExists = Object.values(filteredClusters).some(cluster => !!cluster.clusters.length);
        setClusterExists(clusterExists);

        // setProjectHasSharedCluster(
        //     Object.values(allClusters).some(clusterEnv =>
        //         clusterEnv.clusters.some(
        //             cluster => cluster.type === ListKubernetesResponseKubernetesesTypeEnum.Shared
        //         )
        //     )
        // );
        setLoadingClusters(false);
        const defaultEnv = envId ?
            allClusters[envId].env :
            clusterExists ?
                Object.values(filteredClusters).find(obj => !!obj.clusters.length)!.env :
                Object.values(filteredClusters)[0]?.env;

        setDefaultEnvironment(defaultEnv);
        // newClusterName indicates that the user has connected an existing cluster, set it as default
        if (selectDefaultCluster && !!newClusterName) {
            const { env, clusters } = Object.values(filteredClusters).find(({ clusters }) =>
                clusters.find(({ name }) => name === newClusterName)
            )!;
            const clusterInfo = clusters.find(({ name }) => name === newClusterName)!;

            setSelectedEnv({
                ...env,
                cloudProvider: CreateStageRequestCloudProviderEnum.Aws,
                clusterId: clusterInfo.id,
                clusterName: clusterInfo.name,
                clusterType: clusterInfo.type,
                clusterNamespace: undefined
            } as EnvironmentDetails);
            // Select the first radio option if the user already has a connected cluster
            setClusterType("connected");

            // set default cluster if there is at least one cluster
        } else if (clusterExists && !selectedEnv?.clusterId && selectDefaultCluster) {
            const cluster = filteredClusters[defaultEnv.id].clusters[0];
            setSelectedEnv({
                ...defaultEnv,
                clusterId: cluster.id,
                clusterName: cluster.name,
                clusterType: cluster.type
            } as EnvironmentDetails);

            // Select the first radio option if the user already has a connected cluster
            setClusterType("connected");
        }
    }

    useEffect(() => {
        const loadNamespaces = async () => {
            if (selectedEnv?.clusterId) {
                try {
                    setLoadingNamespaces(true);
                    const { data: { namespaces } } = await getKubeService().getNamespaces(selectedEnv.clusterId, currentProject!.id);

                    setNamespaces(namespaces.map(ns => ({
                        id: ns.metadata.name,
                        name: ns.metadata.name
                    })));
                    // select new namespace if the namespaces of the newly selected cluster don't include the current namespace
                    if (!selectedEnv?.clusterNamespace || !namespaces.map(ns => ns.metadata.name).includes(selectedEnv.clusterNamespace)) {
                        handleOnSelectClusterNamespace(namespaces[0].metadata.name)
                    }
                } catch (error) {

                } finally {
                    setLoadingNamespaces(false);
                }
            }
        }

        loadNamespaces();
        // if (connectedDemoCluster) {
        //     handleSelectDestination();
        // }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEnv?.clusterId]);

    const handleOnSelectCluster = (id: string, { type, key }: { type: ListKubernetesResponseKubernetesesTypeEnum, key: string }) => {
        if (selectedEnv?.clusterId !== id) {
            const { env } = Object.values(clusters).find(value => {
                return value.clusters.find(c => c.id === id);
            })!;
            setSelectedEnv(destination => ({
                ...destination,
                ...env,
                clusterId: id,
                clusterName: key,
                clusterType: type,
            }) as EnvironmentDetails);
            handleSetTemplateState("editing");
        }
    }

    const handleOnSelectClusterNamespace = (namespace: string) => {
        if (selectedEnv?.clusterNamespace !== namespace) {
            setSelectedEnv(destination => ({
                ...destination,
                clusterNamespace: namespace
            }) as EnvironmentDetails);
            if (validationError) {
                setValidationError(undefined);
            }
            handleSetTemplateState("editing");
        }
    }

    // const DemoClusterModalContent = (
    //     <Space direction='vertical'>
    //         <div>
    //             You will get 500MB of RAM on our free Kubernetes cluster. This cluster should only be used for development purposes.
    //         </div>
    //         <div>
    //             To run <b>production applications</b>, create a new cluster using our <a href="/templates">Templates</a>.
    //         </div>
    //         {/* let the user choose an env only if there is no envId and there is more than 1 env in project */}
    //         {/* otherwise use the defaultEnvironment */}
    //         {!envId && Object.values(clusters).length > 1 &&
    //             <Card>
    //                 <Space>
    //                     Environment:
    //                     <Dropdown
    //                         trigger={["click"]}
    //                         placement="bottomRight"
    //                         overlay={
    //                             <Menu placeholder="Select environment">
    //                                 {
    //                                     Object.values(clusters).map(({ env }) => (
    //                                         <Menu.Item key={env.id} onClick={() => setDefaultEnvironment(env)}>
    //                                             {env.name}
    //                                         </Menu.Item>
    //                                     ))
    //                                 }
    //                             </Menu>
    //                         }
    //                     >
    //                         <Button>
    //                             {defaultEnvironment ? defaultEnvironment.name : "Select environment"} <DownOutlined />
    //                         </Button>
    //                     </Dropdown>
    //                 </Space>
    //             </Card>
    //         }
    //     </Space>
    // );

    // useEffect(() => {
    //     if (modal) {
    //         modal.update({
    //             content: DemoClusterModalContent,
    //             onOk: handleRequestDemoCluster
    //         })
    //     }
    // }, [defaultEnvironment]);

    // const showRequestClusterConfirm = () => {
    //     const modal = Modal.confirm({
    //         title: "Deploy on our Free Cluster",
    //         content: DemoClusterModalContent,
    //         okText: "Create Demo Cluster",
    //         centered: true,
    //         onOk: handleRequestDemoCluster
    //     });
    //     setModal(modal);
    // }

    // const handleRequestDemoCluster = async () => {
    //     trackTemplateKubernetesRequestShared(templateName);
    //     setModal(undefined);
    //     const clusterName = "DemoCluster";
    //     const clusterId = `${defaultEnvironment!.id}-${clusterName}`;
    //     // clusterNamespace must consist of lower case alphanumeric characters or '-'
    //     // env.id contains upper case characters
    //     // projectId will be used instead
    //     const clusterNamespace = currentProject!.id;

    //     const { data: { dockerRegistries } } = await getPipelinesService().listDockerRegistries(currentProject!.id);
    //     const existingRegistry = dockerRegistries?.find(r => r.type === "ecr");

    //     const [_, createdKubernetes] = await Promise.allSettled([
    //         !existingRegistry && getPipelinesService().addSharedDockerRegistry(currentProject!.id),
    //         getKubeService().createKubernetes(
    //             currentProject!.id,
    //             {
    //                 name: clusterName,
    //                 type: CreateKubernetesRequestTypeEnum.Shared,
    //                 stageId: defaultEnvironment!.id
    //             }
    //         )
    //     ]);
    //     if (createdKubernetes.status === "fulfilled") {
    //         notification.success({
    //             message: `Cluster connected`,
    //             description: `Cluster ${clusterName} was successfully connected`,
    //         });

    //         await loadData(false);

    //         setConnectedDemoCluster(true);
    //         setClusterType("connected");
    //         setSelectedDestination({
    //             ...selectedDestination!,
    //             clusterId,
    //             clusterNamespace,
    //             type: ListKubernetesResponseKubernetesesTypeEnum.Shared
    //         });
    //     } else {
    //         notification.error({
    //             message: "Error connecting cluster",
    //             description: createdKubernetes.reason.response.data.message
    //         });
    //     }
    // }

    const handleActiveStepChange = () => {
        setCurrentTemplateState(currentTemplateState => ({
            ...currentTemplateState,
            config: "editing",
            cluster: latestTemplateStep.index > 2 ? "saved" : "editing"
        }))
    }

    const handleSelectDestination = () => {
        // create new eks option is chosen but there is no selected environment or cluster name
        if (clusterType === "own" && !creatingCluster) {
            setValidationError(<div>Please choose one of the options for creating/connecting a cluster <b><i>or</i></b> if you have already connected clusters choose one of them.</div>);
            return;
        }
        if (clusterType === "connected" && (!selectedEnv?.clusterId || !selectedEnv?.clusterNamespace)) {
            setValidationError(<div>Please select cluster and namespace <b><i>or</i></b> choose the option to create a new Kubernetes cluster.</div>);
            return;
        }
        trackTemplateKubernetesSave(templateName);
        // creatingCluster is true when 'Create Amazon EKS Cluster' is chosen and cluster name, env and aws account are provided
        if (creatingCluster) {
            onSelect?.({
                id: selectedEnv!.id,
                name: selectedEnv!.name,
                accountId: selectedEnv!.accountId,
                region: selectedEnv!.region,
                cloudProvider: CreateStageRequestCloudProviderEnum.Aws
            })
        } else if (selectedEnv) {
            onSelect?.({
                ...selectedEnv,
                cloudProvider: CreateStageRequestCloudProviderEnum.Aws,
            });
        }
        handleSetTemplateState("saved");
    }

    const FeedbackMessage = () => {
        let content, type: "error" | "domain-info";
        if (validationError) {
            type = "error";
            content = validationError;
        }
        else if (clusterType === "own" && creatingCluster) {
            type = "domain-info";
            content = <div>Your app will be deployed to the <b>{clusterAppName}</b> cluster right after the cluster has been successfully deployed in the <b>{selectedEnv?.name}</b> environment</div>
        }
        else if (clusterType === "connected" && selectedEnv?.clusterId && selectedEnv?.clusterNamespace) {
            type = "domain-info";
            content = <div>Your app will be deployed to the <b>{selectedEnv?.clusterName}</b> cluster under the <b>{selectedEnv?.clusterNamespace}</b> namespace</div>
        } else {
            return null;
        }

        return <MessageCard
            type={type}
            text={content}
            showIcon={false}
            style={{ marginTop: 24 }}
        />
    }
    return (
        <>
            {
                !defaultEnvironment &&
                (
                    !loadingClusters ?
                        <CreateEnvironment onSuccessfullyCreatedStage={loadData} /> :
                        <Card className={cardClassName}>
                            <Card.Meta
                                title={
                                    <>
                                        <span>Choose Where To Deploy</span>
                                        <ExplanationButton
                                            content={<SelectClusterExplanation />}
                                        />
                                    </>
                                }
                                description="Cluster is the infrastructure that runs your container applications."
                            />
                            <Divider />
                            <Skeleton />
                        </Card>
                )
            }
            {
                !!defaultEnvironment &&
                <Card className={cardClassName}>
                    <Card.Meta
                        title={
                            <>
                                <span>Choose Where To Deploy</span>
                                <ExplanationButton
                                    content={<SelectClusterExplanation />}
                                />
                            </>
                        }
                        description="Cluster is the infrastructure that runs your container applications."
                    />
                    <Divider />

                    {
                        loadingClusters ? <Skeleton /> :
                            <Space direction="vertical">
                                <Radio.Group
                                    onChange={(e) => setClusterType(e.target.value)}
                                    value={clusterType}
                                >
                                    <Space direction="vertical" size="large">
                                        {clusterExists &&
                                            <Radio value="connected" checked>
                                                <b>
                                                    Choose an already connected cluster
                                                </b>
                                                <Space direction="vertical">
                                                    <div>
                                                        <small>
                                                            Select a cluster from the list of already connected clusters in your project.
                                                        </small>
                                                    </div>
                                                    {clusterType === "connected" &&
                                                        <span onClick={(e) => {
                                                            // there is a bug in antd when nesting a Select inside a Radio
                                                            // (Select is immediately closed upon opening) so this is a workaround
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}>
                                                            <Row gutter={24}>
                                                                <Col xs={24} md={12}>
                                                                    <Select
                                                                        showSearch
                                                                        style={{ width: "100%", padding: "10px 0" }}
                                                                        placeholder="Select Cluster"
                                                                        value={selectedEnv?.clusterId}
                                                                        onSelect={handleOnSelectCluster}
                                                                    // disabled={connectedDemoCluster}
                                                                    >
                                                                        {
                                                                            Object.entries(clusters).map(([key, value]) => (
                                                                                value.clusters.length &&
                                                                                <Select.OptGroup key={key} label={`${value.env.name} environment`}>
                                                                                    {
                                                                                        value.clusters.map(cluster => (
                                                                                            <Select.Option value={cluster.id} type={cluster.type} key={cluster.name}>
                                                                                                {cluster.name}
                                                                                            </Select.Option>
                                                                                        ))
                                                                                    }
                                                                                </Select.OptGroup>
                                                                            ))
                                                                        }
                                                                    </Select>
                                                                </Col>
                                                                <Col xs={24} md={12} /*hidden={connectedDemoCluster} */>
                                                                    <Tooltip
                                                                        title={
                                                                            selectedEnv?.clusterType === ListKubernetesResponseKubernetesesTypeEnum.Shared ?
                                                                                "Cannot change the namespace that was allocated from our free cluster." :
                                                                                undefined
                                                                        }
                                                                    >
                                                                        <Select
                                                                            showSearch
                                                                            style={{ width: "100%", padding: "10px 0" }}
                                                                            placeholder="Select cluster namespace"
                                                                            loading={loadingNamespaces}
                                                                            filterOption={(input, option: any) => {
                                                                                return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                                                            }}
                                                                            filterSort={(optionA, optionB) => {
                                                                                return optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
                                                                            }}
                                                                            disabled={loadingNamespaces || selectedEnv?.clusterType === ListKubernetesResponseKubernetesesTypeEnum.Shared}
                                                                            value={selectedEnv?.clusterNamespace}
                                                                            onSelect={handleOnSelectClusterNamespace}
                                                                        >
                                                                            {
                                                                                namespaces.map(ns => (
                                                                                    <Select.Option value={ns.id} key={ns.id}>{ns.name}</Select.Option>
                                                                                ))
                                                                            }
                                                                        </Select>
                                                                    </Tooltip>
                                                                </Col>
                                                            </Row>
                                                        </span>
                                                    }
                                                </Space>
                                            </Radio>
                                        }

                                        <Radio value="own" checked>
                                            <b>
                                                Create new Kubernetes Cluster on AWS
                                            </b>
                                            <Space direction="vertical">
                                                <div>
                                                    <small>
                                                        {"Get full ownership of the infrastructure and data for your production apps by creating EKS cluster or "}
                                                        <a href="https://docs.microtica.com/connect-an-existing-kubernetes-cluster" target="_blank" rel="noreferrer">connect an existing cluster</a>
                                                    </small>
                                                </div>

                                                {
                                                    clusterType === "own" &&
                                                    <Space style={{ flexWrap: "wrap" }}>
                                                        <Button
                                                            type="primary"
                                                            onClick={() => {
                                                                if (!clusterAppName) {
                                                                    setSelectedEnv(undefined);
                                                                }
                                                                setShowCreateClusterModal(true);
                                                            }}
                                                        >
                                                            Create Amazon EKS Cluster
                                                        </Button>
                                                        or
                                                        <Button onClick={() => {
                                                            setCreatingCluster(false);
                                                            setShowConnectClusterModal(true);
                                                            trackKubeConnectInit();
                                                        }}>
                                                            Connect Existing Cluster
                                                        </Button>
                                                    </Space>
                                                }
                                            </Space>
                                        </Radio>
                                        {/* {
                                            !projectHasSharedCluster &&
                                            <Radio value="shared">
                                                <b>
                                                    Microtica Kubernetes cluster
                                                </b>
                                                <Space direction="vertical">
                                                    <div>
                                                        <small>
                                                            You will get 500MB of RAM on our free shared Kubernetes cluster. This cluster should only be used for development purposes.
                                                        </small>
                                                    </div>
                                                    <div>
                                                        {
                                                            clusterType === "shared" &&
                                                            <Button
                                                                onClick={showRequestClusterConfirm}
                                                                disabled={disabled}
                                                                type="primary">
                                                                Get Free Cluster
                                                            </Button>
                                                        }
                                                    </div>
                                                </Space>
                                            </Radio>
                                        } */}
                                    </Space>
                                </Radio.Group>
                                <Space>
                                </Space>
                            </Space>
                    }
                    <FeedbackMessage />
                    <Divider />
                    <div className="flex-justify-space-between flex-align-center">
                        <Button onClick={handleActiveStepChange}>
                            Back
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleSelectDestination}
                            disabled={
                                currentTemplateState["cluster"] === "disabled"
                            }
                        >
                            Next
                        </Button>
                    </div>
                    {
                        showConnectClusterModal &&
                        <ConnectClusterModal
                            envId={envId}
                            environments={Object.values(clusters).map(({ env }) => env)}
                            visible={showConnectClusterModal}
                            onCancel={() => setShowConnectClusterModal(false)}
                            onOk={(newClusterName) => {
                                trackTemplateKubernetesConnectExisting(templateName);
                                loadData(true, newClusterName);
                                setShowConnectClusterModal(false);
                                setSelectedEnv(undefined);
                            }}
                        />
                    }
                    {
                        // Workaround. The modal is not unmouting when closed so we need to unmount the whole component using 'modalVisible'
                        // We have a process of periodic API calls which remain active even if when the modal closes
                        showCreateClusterModal &&
                        <CreateClusterModal
                            envId={envId}
                            modalVisible={showCreateClusterModal}
                            setModalVisible={setShowCreateClusterModal}
                            selectedEnv={selectedEnv}
                            setSelectedEnv={setSelectedEnv}
                            clusterAppName={clusterAppName}
                            setClusterAppName={setClusterAppName}
                            onOk={() => {
                                setCreatingCluster(true);
                                if (validationError) {
                                    setValidationError(undefined);
                                }
                                trackTemplateKubernetesAwsCreate(templateName);
                            }}
                            onCancel={() => {
                                setCreatingCluster(false);
                                setSelectedEnv(undefined);
                                setClusterAppName(undefined);
                            }}
                        />
                    }
                </Card>
            }
        </>
    )
}

export default ClusterTemplateModule;